import React, { useEffect, useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from './SpectatorPerspectiveSelector';

function getCellKey(row, col) {
  return `${row}-${col}`;
}

function CellButton({ value, hidden, flagged, exploded, finishRevealed, size, onClick, onContextMenu, disabled }) {
  const palette = {
    1: 'text-blue-600 dark:text-blue-300',
    2: 'text-green-700 dark:text-green-300',
    3: 'text-red-600 dark:text-red-300',
    4: 'text-indigo-700 dark:text-indigo-300',
    5: 'text-rose-700 dark:text-rose-300',
    6: 'text-cyan-700 dark:text-cyan-300',
    7: 'text-gray-800 dark:text-gray-100',
    8: 'text-slate-600 dark:text-slate-300',
  };

  let content = '';
  let classes = 'border border-slate-300 dark:border-slate-700 font-semibold flex items-center justify-center select-none transition-colors '; 
  if (hidden) {
    classes += flagged
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
      : 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700';
    content = flagged ? 'F' : '';
  } else if (exploded || value === 'M') {
    // Always use a red background for mines in dark mode
    if (finishRevealed) {
      classes += 'bg-fuchsia-100 text-fuchsia-800 dark:bg-rose-900/80 dark:text-rose-200';
    } else {
      classes += 'bg-rose-200 text-rose-800 dark:bg-rose-900/80 dark:text-rose-200';
    }
    content = (
      <img
        src="/icons/mine.svg"
        alt="Mine"
        className="pointer-events-none select-none"
        style={{ width: Math.max(14, Math.floor(size * 0.66)), height: Math.max(14, Math.floor(size * 0.66)) }}
        draggable="false"
      />
    );
  } else {
    classes += finishRevealed
      ? 'bg-cyan-50 dark:bg-cyan-950/30'
      : 'bg-white dark:bg-slate-900';
    content = value === 0 ? '' : String(value ?? '');
    classes += ` ${palette[value] || 'text-slate-800 dark:text-slate-100'}`;
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`${classes} ${disabled ? 'cursor-default opacity-80' : 'cursor-pointer'}`}
      style={{ width: size, height: size, fontSize: Math.max(12, Math.floor(size * 0.52)), lineHeight: 1 }}
    >
      {content}
    </button>
  );
}

function MinesweeperGame({ roomId, gameState = {}, gamePlayers = [], user = {}, socket, isSpectator = false }) {
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const [inputMode, setInputMode] = useState('reveal');

  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!perspectiveOptions.length) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [isSpectator, perspectiveOptions, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  const viewedPlayerId = useMemo(() => {
    const progress = gameState?.playerProgress || {};
    for (const candidate of perspectiveIds || []) {
      if (progress[candidate]) return candidate;
    }
    if (!isSpectator) {
      return socket?.id || user?.socketId || user?.userId || null;
    }
    return null;
  }, [gameState?.playerProgress, perspectiveIds, isSpectator, socket?.id, user?.socketId, user?.userId]);

  const board = gameState?.board || { rows: 0, cols: 0, mineCount: 0, safeCellCount: 0 };
  const playerProgress = useMemo(() => gameState?.playerProgress || {}, [gameState?.playerProgress]);
  const myProgress = viewedPlayerId ? playerProgress[viewedPlayerId] || null : null;
  const rows = Number(board.rows) || 0;
  const cols = Number(board.cols) || 0;
  const visibleGrid = myProgress?.visibleGrid || Array.from({ length: rows }, () => Array(cols).fill(null));
  const revealedBoard = gameState?.revealedBoard || null;
  const finished = !!gameState?.finished;
  const grid = useMemo(() => {
    if (!finished || !revealedBoard) return visibleGrid;
    return visibleGrid.map((rowValues, rowIndex) => rowValues.map((value, colIndex) => (
      value !== null ? value : revealedBoard[rowIndex]?.[colIndex] ?? null
    )));
  }, [finished, revealedBoard, visibleGrid]);
  const flagged = myProgress?.flagged || {};
  const myStatus = myProgress?.status || 'active';
  const canInteract = !isSpectator && !!myProgress && !finished && myStatus === 'active';

  const cellSize = useMemo(() => {
    if (cols >= 16) return 24;
    if (cols >= 12) return 28;
    return 28;
  }, [cols]);

  const playerRows = useMemo(() => {
    const serverPlayers = Array.isArray(gameState?.players) && gameState.players.length > 0 ? gameState.players : gamePlayers;
    return serverPlayers.map((player) => {
      const playerId = player.socketId || player.userId || player.id;
      const progress = playerProgress[playerId] || {};
      return {
        id: playerId,
        username: player.username || 'Player',
        revealedCount: Number(progress.revealedCount) || 0,
        status: progress.status || 'active',
        score: Number(gameState?.scores?.[playerId]) || 0,
        isViewed: playerId === viewedPlayerId,
        isWinner: Array.isArray(gameState?.winners) && gameState.winners.includes(playerId),
      };
    });
  }, [gamePlayers, gameState?.players, gameState?.scores, gameState?.winners, playerProgress, viewedPlayerId]);

  const winnerNames = useMemo(() => {
    const winners = Array.isArray(gameState?.winners) ? gameState.winners : [];
    return winners.map((winnerId) => {
      const player = playerRows.find((entry) => entry.id === winnerId);
      return player?.username || 'Player';
    });
  }, [gameState?.winners, playerRows]);

  const submitCellAction = (row, col, mode = inputMode) => {
    if (!canInteract) return;
    const action = mode === 'flag'
      ? { type: 'toggle_flag', row, col }
      : { type: 'reveal', row, col };
    submitAction(action).catch(() => {});
  };

  return (
    <div className="p-4 text-slate-900 dark:text-slate-100">
      {isSpectator && perspectiveOptions.length > 0 ? (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ''}
          onChange={setSpectatorViewPlayerId}
          className="max-w-sm mb-4"
        />
      ) : null}

      <div className="flex flex-col gap-4">
        <div className="flex-1 min-w-0">
          <div className="rounded-2xl border border-slate-300 bg-slate-100 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="text-sm text-slate-600 dark:text-slate-300">
                  {(board.totalCells || rows * cols || 0)} cells · {board.mineCount || 0} mines
                </div>
              <div className="text-sm text-right">
                <div>Revealed: <span className="font-semibold">{myProgress?.revealedCount || 0}</span> / {board.safeCellCount || 0}</div>
                <div className={`${myStatus === 'out' ? 'text-rose-600 dark:text-rose-300' : myStatus === 'won' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-300'}`}>
                  Status: {myStatus === 'out' ? 'Out' : myStatus === 'won' ? 'Won' : 'Active'}
                </div>
              </div>
            </div>

            {!isSpectator ? (
              <div className="mb-4 flex items-center gap-2">
                Mode:
                <button
                  type="button"
                  className={`rounded px-3 py-2 text-sm font-medium ${inputMode === 'reveal' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-100'}`}
                  onClick={() => setInputMode('reveal')}
                >
                  Reveal
                </button>
                <button
                  type="button"
                  className={`rounded px-3 py-2 text-sm font-medium ${inputMode === 'flag' ? 'bg-amber-500 text-slate-950' : 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-100'}`}
                  onClick={() => setInputMode('flag')}
                >
                  Flag
                </button>
              </div>
            ) : null}

            <div className="overflow-x-auto pb-2">
              <div
                className="mx-auto grid gap-px rounded-lg bg-slate-400/30 p-px dark:bg-slate-700/40"
                style={{ gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`, width: `${cols * cellSize + Math.max(0, cols - 1) + 2}px` }}
              >
                {grid.map((rowValues, rowIndex) => rowValues.map((value, colIndex) => {
                  const key = getCellKey(rowIndex, colIndex);
                  const wasHiddenBeforeFinish = visibleGrid[rowIndex]?.[colIndex] === null;
                  const hidden = value === null;
                  const isExploded = value === 'M';
                  return (
                    <CellButton
                      key={key}
                      value={value}
                      hidden={hidden}
                      flagged={!!flagged[key]}
                      exploded={isExploded}
                      finishRevealed={finished && !hidden && wasHiddenBeforeFinish}
                      size={cellSize}
                      disabled={!canInteract && !isSpectator}
                      onClick={() => submitCellAction(rowIndex, colIndex)}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        submitCellAction(rowIndex, colIndex, 'flag');
                      }}
                    />
                  );
                }))}
              </div>
            </div>

            {finished ? (
              <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100">
                {winnerNames.length === 0
                  ? 'Game over.'
                  : winnerNames.length === 1
                    ? `${winnerNames[0]} wins.`
                    : `Draw between ${winnerNames.join(', ')}.`}
              </div>
            ) : null}
          </div>
        </div>

        <div className="w-full">
          <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-300 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h4 className="font-semibold mb-3">Players</h4>
            <div className="space-y-2">
              {playerRows.map((player) => (
                <div
                  key={player.id}
                  className={`rounded-lg border px-3 py-2 ${player.isViewed ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-950/40' : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{player.username}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Wins: {player.score}</div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <div>{player.revealedCount} safe cells</div>
                    <div className={`${player.status === 'out' ? 'text-rose-600 dark:text-rose-300' : player.status === 'won' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-300'}`}>
                      {player.status === 'out' ? 'Out' : player.status === 'won' ? 'Won' : 'Active'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

MinesweeperGame.Guide = function MinesweeperGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Minesweeper</h3>
      <p>Everyone races on the same hidden minefield.</p>
      <p>Tap a square to reveal it. Numbers show how many mines touch that square.</p>
      <p>Use Flag mode, or right-click on desktop, to mark a square you think is dangerous.</p>
      <p>If you hit a mine, you are out for the rest of the round.</p>
      <p>If someone clears all safe squares, they win immediately. If everyone hits a mine, the player who revealed the most safe squares wins.</p>
    </div>
  );
};

export default MinesweeperGame;